import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { useMemo, useState } from "react";
import { CatResultsResponse } from "../../../client/types.gen";
import { SafeReportResponse } from "../Conversion";
import { LayerPerilTab } from "./LayerPerilTab";
import { SelectedModel } from "./types";
import { useCatResults } from "./useCatResults";
import styles from "./CatModelingMobile.module.css";

// Only the models that produce an AAL-by-layer/peril table (Rater and Nearmap
// have their own desktop-only views and are omitted on mobile).
const AAL_MODELS: { value: SelectedModel; label: string }[] = [
  { value: "rms_elt", label: "RMS" },
  { value: "rms_22", label: "RMS 22" },
  { value: "spatialkat", label: "KatRisk" },
  { value: "icm_ylt", label: "ICM" },
];

const modelHasResults = (
  data: CatResultsResponse | null,
  model: SelectedModel
): boolean => {
  switch (model) {
    case "rms_elt":
      return (data?.rms_elt?.aggregate?.layers?.length ?? 0) > 0;
    case "rms_22":
      return (data?.rms_22?.aggregate?.layers?.length ?? 0) > 0;
    case "icm_ylt":
      return (data?.icm_ylt?.aggregate?.layers?.length ?? 0) > 0;
    case "spatialkat":
      return (data?.spatialkat?.aggregate?.layers?.length ?? 0) > 0;
    default:
      return false;
  }
};

/**
 * View-only Modeling tab for phones: pick a provider and view its AAL-by-layer/
 * peril table. No run/edit controls — modeling is initiated on desktop.
 */
export const CatModelingMobile = ({
  specialtyPropertyInfo,
}: {
  specialtyPropertyInfo: SafeReportResponse;
}) => {
  const { data, isLoading } = useCatResults(specialtyPropertyInfo.report_id);
  const layerInfoById = useMemo(
    () =>
      new Map(
        specialtyPropertyInfo.report_json.layer_info.map((layer) => [
          layer.layer_id!,
          layer,
        ])
      ),
    [specialtyPropertyInfo]
  );
  const [selectedModel, setSelectedModel] = useState<SelectedModel>("rms_elt");
  const [showAllLayers, setShowAllLayers] = useState(false);

  const availableOptions = AAL_MODELS.filter((m) =>
    modelHasResults(data, m.value)
  );
  const selectedHasResults = modelHasResults(data, selectedModel);

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <ProgressSpinner style={{ width: "40px", height: "40px" }} />
      </div>
    );
  }

  if (availableOptions.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.note}>
          No modeling results yet — run modeling on desktop.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <Dropdown
          value={selectedModel}
          options={AAL_MODELS}
          optionLabel="label"
          optionValue="value"
          onChange={(e) => e.value && setSelectedModel(e.value)}
          className={styles.dropdown}
        />
        <div className={styles.toggle}>
          <Checkbox
            inputId="cat-show-all-layers"
            checked={showAllLayers}
            onChange={(e) => setShowAllLayers(!!e.checked)}
          />
          <label htmlFor="cat-show-all-layers">Show all layers</label>
        </div>
      </div>
      {selectedHasResults ? (
        <LayerPerilTab
          selectedModel={selectedModel}
          rmsEltResultsData={data}
          rms22ResultsData={data}
          icmYltResultsData={data}
          spatialKatResultsData={data}
          layerInfoById={layerInfoById}
          showAllLayers={showAllLayers}
          onShowAllLayersChange={setShowAllLayers}
          renderToggle={false}
        />
      ) : (
        <div className={styles.note}>No results for this model.</div>
      )}
    </div>
  );
};
